import { Badge, Card, Tag } from "antd";
import {
  MailOutlined,
  PhoneOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UserOutlined,
  IdcardOutlined,
  MobileOutlined,
  SettingOutlined,
  ContactsOutlined,
} from "@ant-design/icons";
import { Row, Col } from "react-bootstrap";
import trans, { formatTimeFromDate, formatTimeOnly } from "../../../../utils";

export default function TechnicianProfile({ data }) {
  const getFileIconUrl = (type = "") => {
    const lowerType = type.toLowerCase();
    if (lowerType === "image/png") {
      return `${process.env.PUBLIC_URL}/assets/PNG.svg`;
    } else if (lowerType === "image/jpg" || lowerType === "image/jpeg") {
      return `${process.env.PUBLIC_URL}/assets/JPG.svg`;
    } else if (lowerType === "application/pdf") {
      return `${process.env.PUBLIC_URL}/assets/PDF.svg`;
    } else {
      return `${process.env.PUBLIC_URL}/assets/Excel.svg`;
    }
  };

  const getContractTypeColor = (type) => {
    switch (type) {
      case "full_time":
        return "green";
      case "part_time":
        return "orange";
      case "contract":
        return "blue";
      default:
        return "default";
    }
  };

  const getDeviceStatusColor = (status) => {
    switch (status) {
      case "assigned":
        return "green";
      case "unassigned":
        return "red";
      case "pending":
        return "orange";
      default:
        return "default";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return trans("Not specified");
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div
      className="profile-wrapper"
      style={{ maxHeight: "70vh", overflowY: "auto" }}
    >
      {/* Basic Info */}
      <Card className="mb-3">
        <div className="d-flex align-items-center gap-2 mb-3">
          <UserOutlined style={{ color: "green" }} />
          <h5 className="mb-0">{trans("Basic Information")}</h5>
        </div>
        <Row className="gy-3">
          <Col md={6}>
            <div className="d-flex align-items-center gap-2">
              <div>
                <small className="text-muted">
                  <MailOutlined className="text-secondary" /> {trans("Email")}
                </small>
                <p className="mb-0">{data.email}</p>
              </div>
            </div>
          </Col>
          <Col md={6}>
            <div className="d-flex align-items-center gap-2">
              <div>
                <small className="text-muted">
                  <PhoneOutlined className="text-secondary" />{" "}
                  {trans("phone number")}
                </small>
                <p className="mb-0">{data.phone}</p>
              </div>
            </div>
          </Col>
          <Col md={6}>
            <div className="d-flex align-items-center gap-2">
              <div>
                <small className="text-muted">
                  <IdcardOutlined className="text-secondary" />{" "}
                  {trans("National ID")}
                </small>
                <p className="mb-0">
                  {data.national_id || trans("Not specified")}
                </p>
              </div>
            </div>
          </Col>
          <Col md={6}>
            <div className="d-flex align-items-center gap-2">
              <div>
                <small className="text-muted">
                  <ContactsOutlined className="text-secondary" />{" "}
                  {trans("Employee ID")}
                </small>
                <p className="mb-0">{data.employee_id}</p>
              </div>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Work Info */}
      <Card className="mb-3">
        <div className="d-flex align-items-center gap-2 mb-3">
          <CalendarOutlined style={{ color: "green" }} />
          <h5 className="mb-0">{trans("Work Information")}</h5>
        </div>
        <Row className="gy-3">
          <Col md={6}>
            <small className="text-muted text-capitalize">
              {trans("Job Title")}
            </small>
            <p>{data.job_title || trans("Not specified")}</p>
          </Col>
          <Col md={6}>
            <small className="text-muted text-capitalize">
              {trans("department")}
            </small>
            <p>{data.department || trans("Not specified")}</p>
          </Col>
          <Col md={6}>
            <div className="d-flex align-items-center gap-2">
              <div>
                <small className="text-muted text-capitalize">
                  {trans("work location")}
                </small>
                <p>{data.work_location}</p>
              </div>
            </div>
          </Col>
          <Col md={6}>
            <small className="text-muted text-capitalize">
              {trans("Specialization")}
            </small>
            <br />
            <Badge>{data.specialization}</Badge>
          </Col>
          <Col md={6}>
            <small className="text-muted">{trans("Supervisor")}</small>
            <p>{data.supervisor_name}</p>
          </Col>
          <Col md={6}>
            <div className="d-flex align-items-center gap-2">
              <div>
                <small className="text-muted">{trans("On Call Status")}</small>
                <p>
                  {data.on_call ? (
                    <CheckCircleOutlined className="text-success" />
                  ) : (
                    <CloseCircleOutlined className="text-danger" />
                  )}{" "}
                  {data.on_call ? trans("Available") : trans("UnAvailable")}
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Contract Info */}
      <Card className="mb-3">
        <div className="d-flex align-items-center gap-2 mb-3">
          <FileTextOutlined style={{ color: "green" }} />
          <h5 className="mb-0">{trans("Contract Information")}</h5>
        </div>
        <Row className="gy-3">
          <Col md={6}>
            <small className="text-muted">{trans("Contract Type")}</small>
            <br />
            <Tag color={getContractTypeColor(data.contract_type)}>
              {data.contract_type?.replace("_", " ").toUpperCase() ||
                trans("Not specified")}
            </Tag>
          </Col>
          <Col md={6}>
            <small className="text-muted">{trans("Contract Start")}</small>
            <p>{formatDate(data.contract_start)}</p>
          </Col>
          <Col md={6}>
            <small className="text-muted">{trans("Contract End")}</small>
            <p>{formatDate(data.contract_end)}</p>
          </Col>
          <Col md={6}>
            <small className="text-muted">{trans("Visa Expiry")}</small>
            <p>{formatDate(data.visa_expiry)}</p>
          </Col>
          {data.account_end_date && (
            <Col md={6}>
              <small className="text-muted">{trans("Account End Date")}</small>
              <p>{formatDate(data.account_end_date)}</p>
            </Col>
          )}
        </Row>
      </Card>

      {/* Schedule Info */}
      <Card className="mb-3">
        <div className="d-flex align-items-center gap-2 mb-3">
          <ClockCircleOutlined style={{ color: "green" }} />
          <h5 className="mb-0">{trans("Schedule Information")}</h5>
        </div>
        <Row className="gy-3">
          <Col md={6}>
            <small className="text-muted">{trans("Shift Start")}</small>
            <p>{formatTimeFromDate(data.shift_start)}</p>
          </Col>
          <Col md={6}>
            <small className="text-muted">{trans("Shift End")}</small>
            <p>{formatTimeFromDate(data.shift_end)}</p>
          </Col>
        </Row>

        <div className="mt-3">
          <small className="text-muted">{trans("Working Days")}</small>
          <div className="d-flex flex-wrap gap-2 mt-1">
            {data.working_days?.map((day, i) => (
              <Tag key={i} color="green">
                {day}
              </Tag>
            ))}
          </div>
        </div>

        {data.breaks?.length > 0 && (
          <div className="mt-3">
            <small className="text-muted">{trans("Breaks")}</small>
            <div className="d-flex flex-column gap-2 mt-1">
              {data.breaks.map((b, i) => (
                <div
                  key={i}
                  className="d-flex align-items-center gap-2 text-secondary small"
                >
                  <ClockCircleOutlined />
                  <span>
                    {b.start} - {b.end}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Device Info */}
      <Card className="mb-3">
        <div className="d-flex align-items-center gap-2 mb-3">
          <MobileOutlined style={{ color: "green" }} />
          <h5 className="mb-0">{trans("Device Information")}</h5>
        </div>
        <Row className="gy-3">
          <Col md={6}>
            <small className="text-muted">{trans("Device Status")}</small>
            <br />
            <Tag color={getDeviceStatusColor(data.device_status)}>
              {data.device_status?.replace("_", " ").toUpperCase()}
            </Tag>
          </Col>
          <Col md={6}>
            <small className="text-muted">{trans("Assignment Option")}</small>
            <p>
              {data.device_assignment_option?.replace("_", " ") ||
                trans("Not specified")}
            </p>
          </Col>
          {data.device_assigned_at && (
            <Col md={6}>
              <small className="text-muted">
                {trans("Device Assigned At")}
              </small>
              <p>{formatDate(data.device_assigned_at)}</p>
            </Col>
          )}
          {data.hardware_device_name && (
            <Col md={6}>
              <small className="text-muted">{trans("Hardware Device")}</small>
              <p>{data.hardware_device_name}</p>
            </Col>
          )}
          {data.assignment_reason && (
            <Col md={12}>
              <small className="text-muted">{trans("Assignment Reason")}</small>
              <p>{data.assignment_reason}</p>
            </Col>
          )}
        </Row>
      </Card>

      {/* Skills */}
      {data.skills?.length > 0 && (
        <Card className="mb-3">
          <div className="d-flex align-items-center gap-2 mb-3">
            <SettingOutlined style={{ color: "green" }} />
            <h5 className="mb-0">{trans("Skills")}</h5>
          </div>
          <div className="d-flex flex-column gap-3">
            {data.skills.map((skill, i) => (
              <div key={i} className="border rounded p-3">
                <div className="d-flex justify-content-between align-items-center">
                  <strong>{skill.skill || "Unnamed Skill"}</strong>
                  <Badge>{skill.skill_level}</Badge>
                </div>
                {skill.description && (
                  <p className="text-muted small mt-2">{skill.description}</p>
                )}
                {skill.certificate_original_name && (
                  <div className="d-flex align-items-center gap-2 text-secondary small mt-2">
                    <FileTextOutlined />
                    <span>
                      Certificate: {skill.certificate_original_name}
                      {skill.sizeMB && ` (${skill.sizeMB}MB)`}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Documents */}
      {data.documents?.length > 0 && (
        <Card className="mb-3">
          <div className="d-flex align-items-center gap-2 mb-3">
            <FileTextOutlined style={{ color: "green" }} />
            <h5 className="mb-0">{trans("Documents")}</h5>
          </div>
          <div className="d-flex flex-column gap-3">
            {data.documents.map((doc, i) => (
              <div
                key={i}
                className="d-flex justify-content-between align-items-center border p-3 rounded pointer"
                onClick={() =>
                  window.open(
                    `${process.env.REACT_APP_BACKEND_URL}/${doc.path}`,
                    "_blank"
                  )
                }
              >
                <div className="d-flex align-items-center gap-3">
                  <img
                    src={getFileIconUrl(doc.mimeType)}
                    alt={doc.mimeType}
                    width={28}
                  />
                  <div>
                    <strong>{doc.originalName}</strong>
                    <div className="text-muted small">{doc.sizeMB}MB</div>
                  </div>
                </div>
                <DownloadOutlined
                  className="text-secondary pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    const link = document.createElement("a");
                    link.href = `${process.env.REACT_APP_BACKEND_URL}Test/${doc.path}`;
                    link.download = doc.originalName;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                />
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
